import { Legend, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ISummarySalon } from 'features/report/services/types/reportStaff';
import { useMemo } from 'react';
import { formatCurrency } from 'utils/formatCurrency';

interface IncomeChartProps {
  data?: ISummarySalon;
}

const IncomeChart = ({ data }: IncomeChartProps) => {
  const chartData = useMemo(() => {
    const defaultData = [
      { name: 'Services', value: 0, percentage: 0, color: '#808836' },
      { name: 'Products', value: 0, percentage: 0, color: '#4F1787' },
      { name: 'Gift Cards', value: 0, percentage: 0, color: '#346751' },
    ];

    if (!data) {
      return defaultData;
    }

    const total = data.totalSaleService + data.totalSaleProduct + data.totalSaleGiftCard;

    if (total === 0) {
      return defaultData;
    }

    return [
      {
        name: 'Services',
        value: data.totalSaleService,
        percentage: Math.round((data.totalSaleService / total) * 100),
        color: '#808836',
      },
      {
        name: 'Products',
        value: data.totalSaleProduct,
        percentage: Math.round((data.totalSaleProduct / total) * 100),
        color: '#4F1787',
      },
      {
        name: 'Gift Cards',
        value: data.totalSaleGiftCard,
        percentage: Math.round((data.totalSaleGiftCard / total) * 100),
        color: '#346751',
      },
    ];
  }, [data]);

  const allZeros = chartData.every((item) => item.value === 0);

  const displayData = useMemo(() => {
    if (allZeros) {
      return {
        labels: chartData.map((item) => item.name),
        datasets: [
          {
            data: chartData.map(() => 1),
            backgroundColor: chartData.map((item) => item.color),
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      };
    }

    return {
      labels: chartData.map((item) => item.name),
      datasets: [
        {
          data: chartData.map((item) => item.value),
          backgroundColor: chartData.map((item) => item.color),
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    };
  }, [chartData, allZeros]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems: any) => {
            const item = chartData[tooltipItems[0].dataIndex];
            return `${item.name} (${item.percentage}%)`;
          },
          label: (tooltipItem: any) => {
            const item = chartData[tooltipItem.dataIndex];
            return `${formatCurrency(item.value)}`;
          },
        },
        titleFont: {
          size: 14,
          family: 'Poppins',
          weight: 'bold',
        },
        bodyFont: {
          size: 14,
          family: 'Poppins',
        },
        enabled: !allZeros,
      },
      datalabels: {
        display: !allZeros,
        color: '#fff',
        font: {
          weight: 'bold',
          size: 14,
        },
        formatter: (value: any, context: any) => {
          const item = chartData[context.dataIndex];
          return item.percentage > 0 ? `${item.percentage}%` : '';
        },
      },
    },
  };

  return (
    <ChartWrapper>
      <Doughnut
        data={displayData}
        //@ts-ignore
        options={options}
        plugins={[Tooltip, Legend, ChartDataLabels]}
      />
      {allZeros && <ZeroDataOverlay>Not Available</ZeroDataOverlay>}
    </ChartWrapper>
  );
};

export default IncomeChart;

const ChartWrapper = styled.div`
  width: 170px;
  height: 170px;
  display: flex;
  align-items: start;
  justify-content: center;
  position: relative;
`;

const ZeroDataOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  font-weight: 500;
  color: #64748b;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 4px;
  border-radius: 4px;
  pointer-events: none;
`;
